import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../utils/api';
import { bannerImageUrl } from '../utils/bannerImageUrl';
import { HeroSkeleton } from './Skeleton';
import './hero.css';

const SLIDE_DURATION = 5000;

const HeroImage = ({ slide, eager, onLoad }) => {
  const fallback = bannerImageUrl(slide.image, 'desktop');

  return (
    <picture>
      <source media="(min-width: 1440px)" srcSet={bannerImageUrl(slide.image, 'desktop')} />
      <source media="(min-width: 1024px)" srcSet={bannerImageUrl(slide.image, 'laptop')} />
      <source media="(min-width: 768px)" srcSet={bannerImageUrl(slide.image, 'tablet')} />
      <source srcSet={bannerImageUrl(slide.image, 'mobile')} />
      <img
        className="hero-image"
        src={fallback}
        alt={slide.altText || slide.title || 'Featured promotion'}
        width="1920"
        height="720"
        loading={eager ? 'eager' : 'lazy'}
        fetchpriority={eager ? 'high' : 'auto'}
        decoding="async"
        onLoad={onLoad}
      />
    </picture>
  );
};

export default function HeroSlider() {
  const bootstrapRef = useRef(
    typeof window !== 'undefined' && window.__HOMEPAGE_DATA__
      ? (Array.isArray(window.__HOMEPAGE_DATA__.slides) ? window.__HOMEPAGE_DATA__.slides : [])
      : null
  );
  const [slides, setSlides] = useState(() => bootstrapRef.current || []);
  const [loading, setLoading] = useState(() => bootstrapRef.current === null);
  const [index, setIndex] = useState(0);
  const [renderedSlides, setRenderedSlides] = useState(() => new Set([0]));
  const [timerKey, setTimerKey] = useState(0);
  const timerRef = useRef(null);
  const idleRef = useRef(null);

  useEffect(() => {
    if (bootstrapRef.current !== null) return undefined;

    const controller = new AbortController();
    axios.get('/api/sliders', { signal: controller.signal })
      .then(res => setSlides(Array.isArray(res.data) ? res.data : []))
      .catch(error => {
        if (error.code !== 'ERR_CANCELED') console.error('Failed to load banners:', error.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const renderSlide = useCallback(slideIndex => {
    setRenderedSlides(current => {
      if (current.has(slideIndex)) return current;
      return new Set([...current, slideIndex]);
    });
  }, []);

  const prepareNextSlide = useCallback(() => {
    if (slides.length < 2) return;
    const nextIndex = (index + 1) % slides.length;
    const prepare = () => renderSlide(nextIndex);
    idleRef.current = window.requestIdleCallback
      ? window.requestIdleCallback(prepare, { timeout: 1500 })
      : window.setTimeout(prepare, 250);
  }, [index, renderSlide, slides.length]);

  useEffect(() => () => {
    if (idleRef.current === null) return;
    if (window.cancelIdleCallback) window.cancelIdleCallback(idleRef.current);
    else window.clearTimeout(idleRef.current);
  }, []);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    if (slides.length > 1) {
      timerRef.current = setInterval(() => {
        setIndex(current => {
          const nextIndex = (current + 1) % slides.length;
          renderSlide(nextIndex);
          return nextIndex;
        });
      }, SLIDE_DURATION);
    }
  }, [renderSlide, slides.length]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const select = nextIndex => {
    renderSlide(nextIndex);
    setIndex(nextIndex);
    setTimerKey(key => key + 1);
    startTimer();
  };

  if (loading) return <HeroSkeleton />;
  if (slides.length === 0) {
    return <div className="hero-placeholder"><h2>Welcome to our store</h2></div>;
  }

  return (
    <section className="hero-slider" aria-roledescription="carousel" aria-label="Featured promotions">
      {slides.map((slide, slideIndex) => (
        <article
          key={slide._id || slideIndex}
          className={`hero-slide ${slideIndex === index ? 'visible' : ''}`}
          aria-hidden={slideIndex !== index}
        >
          {renderedSlides.has(slideIndex) && (
            <HeroImage
              slide={slide}
              eager={slideIndex === 0}
              onLoad={slideIndex === 0 ? prepareNextSlide : undefined}
            />
          )}
          <div className="hero-overlay">
            <div className="hero-content container">
              {slide.title && <h2>{slide.title}</h2>}
              {slide.subtitle && <p>{slide.subtitle}</p>}
              {(slide.buttonText || slide.ctaText) && (slide.buttonLink || slide.ctaLink) && (
                <a className="btn btn-primary" href={slide.buttonLink || slide.ctaLink} tabIndex={slideIndex === index ? 0 : -1}>
                  {slide.buttonText || slide.ctaText}
                </a>
              )}
            </div>
          </div>
        </article>
      ))}

      {slides.length > 1 && <>
        <button type="button" className="hero-prev" onClick={() => select((index - 1 + slides.length) % slides.length)} aria-label="Previous banner">‹</button>
        <button type="button" className="hero-next" onClick={() => select((index + 1) % slides.length)} aria-label="Next banner">›</button>
        <div className="hero-indicators" aria-label="Choose a banner">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide._id || slideIndex}
              type="button"
              className={`hero-indicator ${slideIndex === index ? 'active' : ''}`}
              onClick={() => select(slideIndex)}
              aria-label={`Show banner ${slideIndex + 1} of ${slides.length}`}
              aria-current={slideIndex === index ? 'true' : undefined}
            >
              <span key={slideIndex === index ? `${index}-${timerKey}` : slideIndex} className="hero-indicator-fill" style={slideIndex === index ? { animationDuration: `${SLIDE_DURATION}ms` } : undefined} />
            </button>
          ))}
        </div>
      </>}
    </section>
  );
}
